import type { DataNode } from 'antd/es/tree';
import type { IFlatNode } from '../../types/file';

export type { IFlatNode };

export interface IFolderNode {
  name: string;
  id: string;
  children?: IFolderNode[];
  disk?: string;
  bucket?: string;
  path?: string;
  size?: number;
  extension?: string;
}

export const PREVIEWABLE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'pdf',
  'md',
  'txt',
  'docx',
];

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];

export const TEXT_EXTENSIONS = ['txt', 'md'];

export const DOCX_EXTENSIONS = ['docx'];

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export const convertFlatToTree = (nodes: IFlatNode[]): IFolderNode => {
  const folderIdToChildren = new Map<string, IFlatNode[]>();

  for (const node of nodes) {
    if (node.parentId) {
      const siblings = folderIdToChildren.get(node.parentId) ?? [];
      siblings.push(node);
      folderIdToChildren.set(node.parentId, siblings);
    }
  }

  const buildNode = (node: IFlatNode): IFolderNode => {
    if (node.folder) {
      const children = (folderIdToChildren.get(node.folder.id) ?? []).map(
        buildNode
      );
      return { id: node.folder.id, name: node.folder.name, children };
    }
    const f = node.file!;
    return {
      id: f.id,
      name: f.name,
      disk: f.disk,
      bucket: f.bucket,
      path: f.path,
      size: f.size,
      extension: f.extension,
    };
  };

  const root = nodes.find(n => n.folder && !n.parentId);
  if (!root) throw new Error('convertFlatToTree: no root folder found');
  return buildNode(root);
};

export const convertToTreeData = (
  node: IFolderNode,
  nodeMap: Map<string, IFolderNode>
): DataNode => {
  const isFolder = Array.isArray(node.children);

  if (!isFolder && node.id) {
    nodeMap.set(node.id, node);
  }

  return {
    key: node.id,
    title: node.name,
    isLeaf: !isFolder,
    children: isFolder
      ? node.children!.map(child => convertToTreeData(child, nodeMap))
      : undefined,
  };
};
